import { useCallback, useContext } from 'react';
import { SessionContext } from '../../context/SessionContext';
import { UserContext } from '../../context/UserContext';
import { useTrackUsage } from '../usage/track';
import { useCurrentSession } from './useCurrentSession';
import { useGetAnswer } from './useGetAnwser';

interface UseSendQuestionResult {
  send: (options: { query: string; seqId?: number; isSuggested?: boolean }) => Promise<void>;
  isLoading: boolean;
}

export const useSendQuestion = (): UseSendQuestionResult => {
  const { sessionInfo, lastCreatedSessionId, setSessionInfo } = useContext(SessionContext);
  const { isLoadingCurrentQuery } = sessionInfo;
  const { sessionId, lastSeqId } = useCurrentSession();
  const { theme } = useContext(UserContext);
  const trackUsage = useTrackUsage();

  const { mutateAsync } = useGetAnswer(() => {
    setSessionInfo((prev) => {
      return { ...prev, isLoadingCurrentQuery: false, currentQuery: '' };
    });
  });

  const send: UseSendQuestionResult['send'] = useCallback(
    async ({ query, seqId, isSuggested }) => {
      if (query.trim()) {
        const nextSeqId = seqId ?? lastSeqId + 1;

        if (seqId) {
          trackUsage.regenerateResponse({
            sessionId,
            seqId: nextSeqId,
          });
        } else {
          trackUsage.submitPrompt({
            sessionId,
            seqId: nextSeqId,
            prompt: query,
            isHistorical: lastCreatedSessionId !== sessionId,
            isSuggested: !!isSuggested,
            theme,
          });

          setSessionInfo((prev) => ({ ...prev, currentQuery: query, isLoadingCurrentQuery: true }));
        }

        await mutateAsync({ query, seqId: nextSeqId, sessionId });
      }
    },
    [mutateAsync, setSessionInfo, sessionId, lastSeqId, lastCreatedSessionId, theme]
  );

  return {
    send,
    isLoading: isLoadingCurrentQuery,
  };
};
